import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Loader2, Lock, ShoppingCart, Tag, User, X } from "lucide-react";
import { API_BASE } from "@/lib/supabase";
import { formatPrice } from "@/components/site/EventCard";
import { navigateTopLevel } from "@/lib/topLevelNavigate";
import { formatRegistrationDayDate } from "@/lib/registrationDays";

const DRAFT_KEY = "svj_order_draft";

const emptyForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company_name: "",
  street: "",
  postal_code: "",
  city: "",
};

function Field({ label, name, form, setForm, type = "text", required, autoComplete, testid, className = "" }) {
  return (
    <label className={`block ${className}`} htmlFor={name}>
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
        {label}
        {required && <span className="text-svj-pink"> *</span>}
      </span>
      <input
        id={name}
        name={name}
        type={type}
        autoComplete={autoComplete}
        value={form[name]}
        required={required}
        data-testid={testid}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        className="mt-1 w-full h-11 px-4 rounded-lg bg-svj-bg border border-svj-border text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan focus:border-transparent text-base"
      />
    </label>
  );
}

function emptyParticipant() {
  return { first_name: "", last_name: "", route_group_id: "", medal_number: "" };
}

function emptyRegistrationUnit(fieldDefs) {
  return {
    event_day_ids: [],
    field_values: Object.fromEntries((fieldDefs || []).map((f) => [f.key, f.field_type === "checkbox" ? false : ""])),
  };
}

function emptyTimeslotUnit(fieldDefs) {
  return {
    // Client-side only (a category is a filter/rate-card tag, see
    // TimeslotBookingUnitBlock) -- never sent to the backend, which only ever
    // needs the chosen slot id(s).
    category_id: "",
    // Usually a single id, but may hold several CONSECUTIVE slots combined
    // into one longer visit (e.g. two 20-minute blocks as one 40-minute
    // appointment) -- the server is the final authority on "consecutive"
    // (_assert_timeslot_ids_form_consecutive_run), this is only a UX nicety.
    timeslot_ids: [],
    field_values: Object.fromEntries((fieldDefs || []).map((f) => [f.key, f.field_type === "checkbox" ? false : ""])),
  };
}

// Sum of a timeslot-booking unit's own chosen slot(s)' prices -- the real
// price for this event type, never the ticket type's own (meaningless)
// price_cents. Used both for the running checkout total and the summary.
function timeslotUnitPriceCents(unit, timeslotsById) {
  return (unit.timeslot_ids || []).reduce((a, id) => a + (timeslotsById[id]?.price_cents || 0), 0);
}

// A field with `depends_on_field_id` only shows once the referenced field's own
// current answer (looked up by its `key` in this unit's `field_values`, via
// `fieldsById`) equals `depends_on_value` -- e.g. "Naam bijrijder" only appears
// once "Met hoeveel personen neem je deel?" is answered "Ik neem 1 bijrijder
// mee". A missing/unresolvable controller (e.g. it was deleted) fails open --
// always show -- matching the backend's own fail-open posture in
// _load_multiday_field_defs. Booleans (a checkbox controller) are compared as
// "true"/"false" text, same as the backend.
//
// A field with `depends_on_timeslot_category_id` instead only shows once THIS
// unit's own chosen timeslot category (categoryId, only ever set inside
// TimeslotBookingUnitBlock) matches -- the two mechanisms are mutually
// exclusive per field (enforced by the admin backend), so only one of the two
// checks below ever actually applies for a given field.
function fieldIsVisible(field, values, fieldsById, categoryId) {
  if (field.depends_on_timeslot_category_id) {
    return field.depends_on_timeslot_category_id === categoryId;
  }
  if (!field.depends_on_field_id) return true;
  const controller = fieldsById[field.depends_on_field_id];
  if (!controller) return true;
  const actual = String(values?.[controller.key] ?? "").trim().toLowerCase();
  const trigger = String(field.depends_on_value ?? "").trim().toLowerCase();
  return actual === trigger;
}

// One registration-unit block (day selection + admin-defined custom fields) per
// individual ticket unit, only ever rendered for multi_day_registration_enabled
// events ("Lichtjestour"-style) — the day-based sibling of ParticipantBlock's
// distance/route-based Companion-event fields.
function RegistrationFieldInput({ field, value, onChange }) {
  const commonClass = "mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white focus:outline-none focus:ring-2 focus:ring-svj-cyan";
  if (field.field_type === "textarea") {
    return <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className={`${commonClass} h-auto`} />;
  }
  if (field.field_type === "select") {
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={commonClass}>
        <option value="">Kies…</option>
        {(field.options || []).map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }
  if (field.field_type === "checkbox") {
    return (
      <label className="mt-2 flex items-center gap-2">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-svj-border bg-svj-bg" />
        <span className="text-xs text-slate-300">Ja</span>
      </label>
    );
  }
  const inputType = field.field_type === "number" ? "number" : field.field_type === "email" ? "email" : field.field_type === "phone" ? "tel" : "text";
  return <input type={inputType} value={value || ""} onChange={(e) => onChange(e.target.value)} className={commonClass} />;
}

function RegistrationUnitBlock({ index, ticketName, value, onChange, days, fieldDefs, fieldsById }) {
  const visibleFieldDefs = fieldDefs.filter((f) => fieldIsVisible(f, value.field_values, fieldsById));
  const toggleDay = (dayId) => {
    const set = new Set(value.event_day_ids);
    if (set.has(dayId)) set.delete(dayId);
    else set.add(dayId);
    onChange({ ...value, event_day_ids: Array.from(set) });
  };

  return (
    <div className="rounded-lg border border-svj-border bg-svj-bg/60 p-4 space-y-3" data-testid={`checkout-registration-unit-${index}`}>
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">
        {ticketName} — ticket {index + 1}
      </div>
      <div>
        <span className="text-xs text-slate-400">Aanmelden voor dag(en) *</span>
        <div className="mt-2 flex flex-wrap gap-2">
          {days.map((d) => {
            const checked = value.event_day_ids.includes(d.id);
            const isFull = !!d.full && !checked;
            const dayDateTime = formatRegistrationDayDate(d);
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => !isFull && toggleDay(d.id)}
                disabled={isFull}
                title={isFull ? "Deze dag is helaas vol" : undefined}
                data-testid={`checkout-registration-day-${index}-${d.id}`}
                className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                  checked
                    ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                    : isFull
                      ? "bg-svj-bg border-svj-border text-slate-600 cursor-not-allowed opacity-60"
                      : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                }`}
              >
                <div className="flex items-center gap-1.5 text-xs font-semibold">
                  {d.label || `Dag ${d.day_number}`}
                  {isFull && (
                    <span className="ml-1 text-[10px] uppercase tracking-widest text-red-400" data-testid={`checkout-registration-day-full-${index}-${d.id}`}>
                      VOL
                    </span>
                  )}
                </div>
                {dayDateTime && (
                  <div className="mt-0.5 flex items-center gap-1 text-[11px] font-normal opacity-80">
                    <Calendar className="h-3 w-3 shrink-0" />
                    {dayDateTime}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {visibleFieldDefs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {visibleFieldDefs.map((f) => (
            <label key={f.key} className="block">
              <span className="text-xs text-slate-400">{f.label}{f.required && " *"}</span>
              <RegistrationFieldInput
                field={f}
                value={value.field_values[f.key]}
                onChange={(v) => onChange({ ...value, field_values: { ...value.field_values, [f.key]: v } })}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// One timeslot-booking block per individual ticket unit ("Sinterklaas
// huisbezoeken"-style events) -- a single-select date -> time-block picker
// (unlike RegistrationUnitBlock's multi-select day toggle), each time block
// showing its own price and "VOL" once full, followed by the same generic
// admin-defined custom fields.
function TimeslotBookingUnitBlock({ index, ticketName, value, onChange, timeslots, categories, fieldDefs, fieldsById }) {
  const visibleFieldDefs = fieldDefs.filter((f) => fieldIsVisible(f, value.field_values, fieldsById, value.category_id));
  // When the event has categories ("Bedrijven" €100/60min, "Particulieren
  // Dronten" €55/20min, ...), a category is selected first and the date/time
  // picker below is filtered to only that category's own slots -- a category
  // is purely a client-side filter dimension, never sent to the backend
  // itself (only the chosen slot id(s) are).
  const relevantSlots = useMemo(
    () => (categories.length === 0 ? timeslots : timeslots.filter((s) => s.category_id === value.category_id)),
    [timeslots, categories, value.category_id],
  );
  const byDate = useMemo(() => {
    const groups = {};
    for (const s of relevantSlots) {
      (groups[s.date] = groups[s.date] || []).push(s);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [relevantSlots]);

  const timeslotIds = useMemo(() => value.timeslot_ids || [], [value.timeslot_ids]);
  // Chosen slot(s), chronologically -- the order they were clicked in never
  // matters, only the real start_time order does (same convention the
  // backend's own _assert_timeslot_ids_form_consecutive_run uses).
  const selectedSlots = useMemo(
    () =>
      timeslotIds
        .map((id) => timeslots.find((s) => s.id === id))
        .filter(Boolean)
        .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")),
    [timeslotIds, timeslots],
  );
  const firstSelected = selectedSlots[0];
  const lastSelected = selectedSlots[selectedSlots.length - 1];

  // Calendar-style picker: with many dates/times this used to render one
  // giant flat list of every slot on every date at once. Now only a compact
  // row of date chips is always shown, and a date's own times only appear
  // once that date is tapped -- a real calendar-day-drilldown, not a list.
  const [selectedDate, setSelectedDate] = useState(firstSelected?.date || "");
  useEffect(() => {
    // The already-chosen slot(s)' date always wins (e.g. re-opening this
    // block after a page reload, or a sibling ticket unit already picked a
    // slot).
    if (firstSelected?.date) setSelectedDate(firstSelected.date);
  }, [firstSelected?.date]);
  useEffect(() => {
    // The category changed (or slots loaded) and the currently expanded date
    // no longer exists in this filtered set -- never leave a stale date open.
    if (selectedDate && !byDate.some(([d]) => d === selectedDate)) {
      setSelectedDate("");
    }
  }, [byDate, selectedDate]);
  const activeDateSlots = useMemo(
    () =>
      [...(byDate.find(([d]) => d === selectedDate)?.[1] || [])].sort((a, b) =>
        (a.start_time || "").localeCompare(b.start_time || ""),
      ),
    [byDate, selectedDate],
  );

  // Tapping a slot either starts a fresh single-slot selection, or -- when
  // it sits immediately next to the current selection within THIS date's own
  // ordered slot list (same "contiguous run" rule the backend enforces,
  // _assert_timeslot_ids_form_consecutive_run) -- extends/shrinks the range
  // by one slot at that end. Tapping anything else (not adjacent, or a slot
  // in the middle of the current range) always replaces the selection with
  // just that one slot, so the range can never end up broken.
  const toggleSlot = (slot) => {
    if (slot.full && !timeslotIds.includes(slot.id)) return;
    const idx = activeDateSlots.findIndex((s) => s.id === slot.id);
    if (!selectedSlots.length) {
      onChange({ ...value, timeslot_ids: [slot.id] });
      return;
    }
    const firstIdx = activeDateSlots.findIndex((s) => s.id === firstSelected.id);
    const lastIdx = activeDateSlots.findIndex((s) => s.id === lastSelected.id);
    if (idx === lastIdx && selectedSlots.length > 1) {
      // Tapping the current last slot again shrinks the range by one.
      onChange({ ...value, timeslot_ids: selectedSlots.slice(0, -1).map((s) => s.id) });
    } else if (idx === firstIdx && selectedSlots.length > 1) {
      onChange({ ...value, timeslot_ids: selectedSlots.slice(1).map((s) => s.id) });
    } else if (idx === lastIdx + 1) {
      onChange({ ...value, timeslot_ids: [...selectedSlots.map((s) => s.id), slot.id] });
    } else if (idx === firstIdx - 1) {
      onChange({ ...value, timeslot_ids: [slot.id, ...selectedSlots.map((s) => s.id)] });
    } else if (idx === firstIdx && selectedSlots.length === 1) {
      // The only selected slot, tapped again -- deselect entirely.
      onChange({ ...value, timeslot_ids: [] });
    } else {
      onChange({ ...value, timeslot_ids: [slot.id] });
    }
  };

  const totalPriceCents = selectedSlots.reduce((a, s) => a + (s.price_cents || 0), 0);

  return (
    <div className="rounded-lg border border-svj-border bg-svj-bg/60 p-4 space-y-3" data-testid={`checkout-timeslot-unit-${index}`}>
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">
        {ticketName} — afspraak {index + 1}
      </div>
      {categories.length > 0 && (
        <div>
          <span className="text-xs text-slate-400">Kies een categorie *</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {categories.map((c) => {
              const checked = value.category_id === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onChange({ ...value, category_id: c.id, timeslot_ids: [] })}
                  data-testid={`checkout-timeslot-category-${index}-${c.id}`}
                  className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                    checked
                      ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                      : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                  }`}
                >
                  <div className="text-xs font-semibold">{c.name}</div>
                  <div className="mt-0.5 text-[11px] font-normal opacity-80">
                    {c.price_cents === 0 ? "Gratis" : formatPrice(c.price_cents)} · {c.duration_minutes} min
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {(categories.length === 0 || value.category_id) && (
      <div>
        <span className="text-xs text-slate-400">Kies een datum *</span>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`checkout-timeslot-dates-${index}`}>
          {byDate.map(([date, slots]) => {
            const dateObj = new Date(`${date}T00:00:00`);
            const allFull = slots.every((s) => s.full);
            const checked = selectedDate === date;
            return (
              <button
                key={date}
                type="button"
                onClick={() => {
                  // A consecutive run can never span two dates -- switching
                  // dates always starts a fresh selection.
                  if (date !== selectedDate) onChange({ ...value, timeslot_ids: [] });
                  setSelectedDate(date);
                }}
                data-testid={`checkout-timeslot-date-${index}-${date}`}
                className={`flex flex-col items-center rounded-xl border px-3 py-1.5 min-w-[56px] transition-colors ${
                  checked
                    ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                    : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                }`}
              >
                <div className="text-[9px] uppercase tracking-widest opacity-70">
                  {dateObj.toLocaleDateString("nl-NL", { weekday: "short" })}
                </div>
                <div className="text-base font-bold leading-tight">{dateObj.getDate()}</div>
                <div className="text-[9px] uppercase tracking-widest opacity-70">
                  {dateObj.toLocaleDateString("nl-NL", { month: "short" })}
                </div>
                {allFull && (
                  <span className="mt-0.5 text-[9px] font-bold uppercase tracking-widest text-red-400">Vol</span>
                )}
              </button>
            );
          })}
          {byDate.length === 0 && <div className="text-xs text-slate-500">Nog geen tijdsloten beschikbaar.</div>}
        </div>
        {selectedDate && (
          <div className="mt-3">
            <span className="text-xs text-slate-400">
              Kies een tijdslot *{" "}
              <span className="normal-case font-normal text-slate-500">
                (tik meerdere aaneengesloten tijdsloten aan voor een langer bezoek)
              </span>
            </span>
            <div className="mt-2 flex flex-wrap gap-2">
              {activeDateSlots.map((s) => {
                const checked = timeslotIds.includes(s.id);
                const isFull = !!s.full && !checked;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleSlot(s)}
                    disabled={isFull}
                    title={isFull ? "Dit tijdslot is helaas vol" : undefined}
                    data-testid={`checkout-timeslot-${index}-${s.id}`}
                    className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                      checked
                        ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                        : isFull
                          ? "bg-svj-bg border-svj-border text-slate-600 cursor-not-allowed opacity-60"
                          : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 text-xs font-semibold">
                      {s.start_time?.slice(0, 5)} - {s.end_time?.slice(0, 5)}
                      {isFull && (
                        <span className="ml-1 text-[10px] uppercase tracking-widest text-red-400">VOL</span>
                      )}
                    </div>
                    <div className="mt-0.5 text-[11px] font-normal opacity-80">
                      {s.price_cents === 0 ? "Gratis" : formatPrice(s.price_cents)}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {selectedSlots.length > 0 && (
          <div className="mt-2 text-xs text-svj-cyan" data-testid={`checkout-timeslot-selected-${index}`}>
            Gekozen: {new Date(`${firstSelected.date}T00:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "long" })},{" "}
            {firstSelected.start_time?.slice(0, 5)} - {lastSelected.end_time?.slice(0, 5)} ·{" "}
            {totalPriceCents === 0 ? "Gratis" : formatPrice(totalPriceCents)}
            {selectedSlots.length > 1 && ` (${selectedSlots.length} tijdsloten samengevoegd)`}
          </div>
        )}
      </div>
      )}
      {visibleFieldDefs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {visibleFieldDefs.map((f) => (
            <label key={f.key} className="block">
              <span className="text-xs text-slate-400">{f.label}{f.required && " *"}</span>
              <RegistrationFieldInput
                field={f}
                value={value.field_values[f.key]}
                onChange={(v) => onChange({ ...value, field_values: { ...value.field_values, [f.key]: v } })}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// One participant block (name + optional route + medal number) per individual ticket
// unit, only ever rendered for Companion-app events (Sprint 02).
function ParticipantBlock({ index, ticketName, value, onChange, routeGroups, routeRequired }) {
  return (
    <div className="rounded-lg border border-svj-border bg-svj-bg/60 p-4 space-y-3" data-testid={`checkout-participant-${index}`}>
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">
        {ticketName} — deelnemer {index + 1}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-slate-400">Voornaam *</span>
          <input
            type="text"
            required
            value={value.first_name}
            onChange={(e) => onChange({ ...value, first_name: e.target.value })}
            className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white focus:outline-none focus:ring-2 focus:ring-svj-cyan"
          />
        </label>
        <label className="block">
          <span className="text-xs text-slate-400">Achternaam *</span>
          <input
            type="text"
            required
            value={value.last_name}
            onChange={(e) => onChange({ ...value, last_name: e.target.value })}
            className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white focus:outline-none focus:ring-2 focus:ring-svj-cyan"
          />
        </label>
        {routeGroups.length > 0 && (
          <label className="block">
            <span className="text-xs text-slate-400">Routekeuze{routeRequired && " *"}</span>
            <select
              required={routeRequired}
              value={value.route_group_id}
              onChange={(e) => onChange({ ...value, route_group_id: e.target.value })}
              className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white focus:outline-none focus:ring-2 focus:ring-svj-cyan"
            >
              <option value="">Kies…</option>
              {routeGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                  {g.distance_km ? ` (${g.distance_km} km)` : ""}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="block">
          <span className="text-xs text-slate-400">Medaillenummer</span>
          <input
            type="text"
            value={value.medal_number}
            onChange={(e) => onChange({ ...value, medal_number: e.target.value })}
            className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white focus:outline-none focus:ring-2 focus:ring-svj-cyan"
          />
        </label>
      </div>
    </div>
  );
}

export default function Checkout() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [participantTermsAccepted, setParticipantTermsAccepted] = useState(false);
  const [organizerTermsAccepted, setOrganizerTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [routeGroups, setRouteGroups] = useState([]);
  const [participantsByType, setParticipantsByType] = useState({});
  const [eventDays, setEventDays] = useState([]);
  const [registrationFields, setRegistrationFields] = useState([]);
  const [registrationUnitsByType, setRegistrationUnitsByType] = useState({});
  const [timeslots, setTimeslots] = useState([]);
  const [timeslotCategories, setTimeslotCategories] = useState([]);
  const [timeslotUnitsByType, setTimeslotUnitsByType] = useState({});
  const [discountCodeInput, setDiscountCodeInput] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [checkingDiscount, setCheckingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState("");
  // Tracks whether the visitor has manually touched the participant-name field on
  // the order's very first registration unit (see the sync effect below) -- once
  // true, the live "keep it in sync with Klantgegevens" behavior stops touching
  // that field, so a deliberate edit (or a deliberate clear, e.g. the buyer isn't
  // attending themselves) is never silently overwritten.
  const [firstParticipantNameEdited, setFirstParticipantNameEdited] = useState(false);
  const autoFilledNameRef = useRef("");

  useEffect(() => {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) {
      toast.error("Kies eerst tickets voordat je naar de kassa gaat");
      navigate("/events");
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      if (!parsed?.items?.length) {
        navigate("/events");
        return;
      }
      setDraft(parsed);
      if (parsed.companion_app_enabled) {
        setParticipantsByType(
          Object.fromEntries(
            parsed.items.map((i) => [i.ticket_type_id, Array.from({ length: i.qty }, emptyParticipant)]),
          ),
        );
      }
      if (parsed.multi_day_registration_enabled) {
        setRegistrationUnitsByType(
          Object.fromEntries(
            parsed.items.map((i) => [i.ticket_type_id, Array.from({ length: i.qty }, () => emptyRegistrationUnit([]))]),
          ),
        );
      }
      if (parsed.timeslot_booking_enabled) {
        setTimeslotUnitsByType(
          Object.fromEntries(
            parsed.items.map((i) => [i.ticket_type_id, Array.from({ length: i.qty }, () => emptyTimeslotUnit([]))]),
          ),
        );
      }
    } catch {
      navigate("/events");
    }
  }, [navigate]);

  // Multi-day registration ("Lichtjestour"-style events) — fetch event days + the
  // admin-defined registration fields, the day-based sibling of the Companion-event
  // route-groups fetch above.
  useEffect(() => {
    if (!draft?.multi_day_registration_enabled) return;
    let ignore = false;
    (async () => {
      try {
        const [daysRes, fieldsRes] = await Promise.all([
          axios.get(`${API_BASE}/events/${draft.event_id}/days`),
          axios.get(`${API_BASE}/events/${draft.event_id}/registration-fields`),
        ]);
        if (!ignore) {
          setEventDays(daysRes.data?.items || []);
          setRegistrationFields(fieldsRes.data?.items || []);
        }
      } catch {
        if (!ignore) {
          setEventDays([]);
          setRegistrationFields([]);
        }
      }
    })();
    return () => {
      ignore = true;
    };
  }, [draft?.multi_day_registration_enabled, draft?.event_id]);

  // Once the field definitions arrive, backfill any missing keys into already-seeded
  // units (never overwriting a value the visitor may already have typed).
  useEffect(() => {
    if (!registrationFields.length) return;
    setRegistrationUnitsByType((prev) => {
      const next = {};
      for (const [typeId, units] of Object.entries(prev)) {
        next[typeId] = units.map((u) => ({
          ...u,
          field_values: {
            ...Object.fromEntries(registrationFields.map((f) => [f.key, f.field_type === "checkbox" ? false : ""])),
            ...u.field_values,
          },
        }));
      }
      return next;
    });
  }, [registrationFields]);

  // Timeslot booking ("Sinterklaas huisbezoeken"-style events) — fetch the
  // public, active-only timeslots for the checkout picker, and reuse the same
  // event_registration_fields endpoint multi-day registration already uses
  // (one generic, event-scoped table, not specific to either feature).
  useEffect(() => {
    if (!draft?.timeslot_booking_enabled) return;
    let ignore = false;
    (async () => {
      try {
        const [slotsRes, categoriesRes, fieldsRes] = await Promise.all([
          axios.get(`${API_BASE}/events/${draft.event_id}/timeslots`),
          axios.get(`${API_BASE}/events/${draft.event_id}/timeslot-categories`),
          axios.get(`${API_BASE}/events/${draft.event_id}/registration-fields`),
        ]);
        if (!ignore) {
          setTimeslots(slotsRes.data?.items || []);
          setTimeslotCategories(categoriesRes.data?.items || []);
          setRegistrationFields(fieldsRes.data?.items || []);
        }
      } catch {
        if (!ignore) {
          setTimeslots([]);
          setTimeslotCategories([]);
          setRegistrationFields([]);
        }
      }
    })();
    return () => {
      ignore = true;
    };
  }, [draft?.timeslot_booking_enabled, draft?.event_id]);

  // Same backfill as above, for timeslot-booking units.
  useEffect(() => {
    if (!registrationFields.length) return;
    setTimeslotUnitsByType((prev) => {
      const next = {};
      for (const [typeId, units] of Object.entries(prev)) {
        next[typeId] = units.map((u) => ({
          ...u,
          field_values: {
            ...Object.fromEntries(registrationFields.map((f) => [f.key, f.field_type === "checkbox" ? false : ""])),
            ...u.field_values,
          },
        }));
      }
      return next;
    });
  }, [registrationFields]);

  const setTimeslotUnit = (typeId, idx, value) => {
    setTimeslotUnitsByType((prev) => {
      const list = [...(prev[typeId] || [])];
      list[idx] = value;
      return { ...prev, [typeId]: list };
    });
  };

  useEffect(() => {
    if (!draft?.companion_app_enabled) return;
    let ignore = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/events/${draft.event_id}/route-groups`);
        if (!ignore) setRouteGroups(data?.items || []);
      } catch {
        if (!ignore) setRouteGroups([]);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [draft?.companion_app_enabled, draft?.event_id]);

  const setParticipant = (typeId, idx, value) => {
    setParticipantsByType((prev) => {
      const list = [...(prev[typeId] || [])];
      list[idx] = value;
      return { ...prev, [typeId]: list };
    });
  };

  const setRegistrationUnit = (typeId, idx, value) => {
    // The very first unit of the very first ticket item is the one the sync
    // effect below keeps pre-filled with the buyer's own name while an order has
    // 2+ tickets -- if this edit touches that exact field with a value other than
    // what we last auto-wrote, the visitor is correcting/clearing it on purpose,
    // so stop auto-syncing it from here on.
    if (
      !firstParticipantNameEdited &&
      participantNameField &&
      idx === 0 &&
      draft?.items?.[0]?.ticket_type_id === typeId &&
      (value.field_values?.[participantNameField.key] || "") !== autoFilledNameRef.current
    ) {
      setFirstParticipantNameEdited(true);
    }
    setRegistrationUnitsByType((prev) => {
      const list = [...(prev[typeId] || [])];
      list[idx] = value;
      return { ...prev, [typeId]: list };
    });
  };

  // Timeslot booking ("Sinterklaas huisbezoeken"-style events) -- the real
  // price comes from each unit's OWN chosen slot, never from the ticket type's
  // own price_cents (which the cart/EventDetail step set before any slot was
  // picked). A unit with no slot chosen yet contributes €0 to this running
  // total -- the backend is always the authoritative source once submitted.
  const timeslotsById = useMemo(() => Object.fromEntries(timeslots.map((s) => [s.id, s])), [timeslots]);

  const totalCents = useMemo(() => {
    if (!draft) return 0;
    if (draft.timeslot_booking_enabled) {
      return Object.values(timeslotUnitsByType)
        .flat()
        .reduce((a, u) => a + timeslotUnitPriceCents(u, timeslotsById), 0);
    }
    return draft.items.reduce((a, i) => a + i.qty * i.unit_price_cents, 0);
  }, [draft, timeslotUnitsByType, timeslotsById]);

  // Timeslot booking: before every ticket unit has an actual slot chosen,
  // totalCents is 0 purely because nothing has a price yet -- not because the
  // order genuinely is free. Showing "Gratis" at that point is misleading (the
  // real price only exists once a slot is picked), so every "free" display
  // below is additionally gated on this for a timeslot-booking event.
  const allTimeslotUnitsSelected = useMemo(() => {
    if (!draft?.timeslot_booking_enabled) return true;
    const units = Object.values(timeslotUnitsByType).flat();
    return units.length > 0 && units.every((u) => u.timeslot_ids?.length);
  }, [draft, timeslotUnitsByType]);

  const totalQty = useMemo(
    () => (draft ? draft.items.reduce((a, i) => a + i.qty, 0) : 0),
    [draft],
  );

  // The one admin-designated "this field is the participant's own name" field
  // (see AdminEventRegistration.jsx's is_participant_name toggle). When the whole
  // order is exactly 1 ticket, that field is redundant with Voornaam/Achternaam
  // under Klantgegevens above -- so it's hidden here and auto-filled from the
  // buyer's own name in onSubmit instead of asked for twice. Any other
  // admin-defined field (allergie, bijrijder, ...) is unaffected and still shown.
  const participantNameField = useMemo(
    () => registrationFields.find((f) => f.is_participant_name) || null,
    [registrationFields],
  );
  const skipParticipantNameField = totalQty === 1 && !!participantNameField;

  // Id -> full field lookup for resolving a field's `depends_on_field_id` back to
  // the controlling field's own `key` (see fieldIsVisible above) -- built from the
  // full, unfiltered list so a conditional field can still reference the
  // is_participant_name field even in the totalQty===1 case where that one field
  // is itself hidden from `fieldDefs`.
  const registrationFieldsById = useMemo(
    () => Object.fromEntries(registrationFields.map((f) => [f.id, f])),
    [registrationFields],
  );

  // With 2+ tickets the participant-name field can't be hidden (it's genuinely
  // ambiguous whose ticket is whose) -- but the buyer is very often also the
  // first participant, so the order's very first registration unit is kept
  // pre-filled with the buyer's own name as a starting point, live, until the
  // visitor edits (or clears) that exact field themselves (tracked via
  // firstParticipantNameEdited in setRegistrationUnit above). Units for every
  // other ticket are never touched by this.
  useEffect(() => {
    if (!participantNameField || totalQty <= 1 || firstParticipantNameEdited) return;
    const firstItem = draft?.items?.[0];
    if (!firstItem) return;
    const fullName = `${form.first_name.trim()} ${form.last_name.trim()}`.trim();
    autoFilledNameRef.current = fullName;
    setRegistrationUnitsByType((prev) => {
      const units = prev[firstItem.ticket_type_id];
      if (!units || !units.length) return prev;
      const current = units[0];
      if ((current.field_values?.[participantNameField.key] || "") === fullName) return prev;
      const nextUnits = [...units];
      nextUnits[0] = { ...current, field_values: { ...current.field_values, [participantNameField.key]: fullName } };
      return { ...prev, [firstItem.ticket_type_id]: nextUnits };
    });
  }, [form.first_name, form.last_name, participantNameField, totalQty, draft, firstParticipantNameEdited]);

  // Mirrors the backend's own all_pay_on_location check in create_order: only when
  // EVERY item in the cart opted in does the order skip online payment — a mixed
  // cart (one pay-on-location + one normal-paid type) still needs full online
  // payment, so this must not be true for that case.
  const allPayOnLocation = useMemo(
    () => !!draft?.items?.length && draft.items.every((i) => i.pay_on_location),
    [draft],
  );

  const applyDiscountCode = async () => {
    const code = discountCodeInput.trim();
    if (!code) return;
    setCheckingDiscount(true);
    setDiscountError("");
    try {
      const { data } = await axios.post(`${API_BASE}/events/${draft.event_id}/discount-codes/check`, {
        code,
        subtotal_cents: totalCents,
      });
      setAppliedDiscount(data);
    } catch (err) {
      setAppliedDiscount(null);
      setDiscountError(err?.response?.data?.detail || err.message || "Ongeldige kortingscode");
    } finally {
      setCheckingDiscount(false);
    }
  };

  const removeDiscountCode = () => {
    setAppliedDiscount(null);
    setDiscountCodeInput("");
    setDiscountError("");
  };

  const payableCents = appliedDiscount ? appliedDiscount.new_total_cents : totalCents;

  // A discount code can bring an otherwise-paid order down to €0 — free is
  // determined by what's actually payable, not the pre-discount subtotal. For
  // a timeslot-booking event, €0 before any slot is chosen is not "free" --
  // it's simply unpriced yet, so this also requires every unit to have an
  // actual slot selected.
  const isFreeOrder = payableCents === 0 && allTimeslotUnitsSelected;
  const skipsOnlinePayment = allPayOnLocation || isFreeOrder;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!draft) return;
    if (!termsAccepted) {
      toast.error("Vink de algemene voorwaarden aan om verder te gaan");
      return;
    }
    if (draft.participant_terms_enabled && !participantTermsAccepted) {
      toast.error("Vink het deelnemersreglement aan om verder te gaan");
      return;
    }
    if (draft.organizer_terms_enabled && !organizerTermsAccepted) {
      toast.error("Vink de aanvullende voorwaarden van de organisator aan om verder te gaan");
      return;
    }
    if (draft.multi_day_registration_enabled) {
      const allUnits = Object.values(registrationUnitsByType).flat();
      if (allUnits.some((u) => !u.event_day_ids.length)) {
        toast.error("Kies minstens één dag voor elk ticket");
        return;
      }
    }
    if (draft.timeslot_booking_enabled) {
      const allTimeslotUnits = Object.values(timeslotUnitsByType).flat();
      if (allTimeslotUnits.some((u) => !u.timeslot_ids?.length)) {
        toast.error("Kies een tijdslot voor elk ticket");
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        event_id: draft.event_id,
        customer: {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          company_name: form.company_name.trim() || null,
          street: form.street.trim() || null,
          postal_code: form.postal_code.trim() || null,
          city: form.city.trim() || null,
        },
        items: draft.items.map((i) => ({
          ticket_type_id: i.ticket_type_id,
          qty: i.qty,
          ...(draft.companion_app_enabled
            ? {
                participants: (participantsByType[i.ticket_type_id] || []).map((p) => ({
                  first_name: p.first_name.trim(),
                  last_name: p.last_name.trim(),
                  route_group_id: p.route_group_id || null,
                  medal_number: p.medal_number.trim() || null,
                })),
              }
            : {}),
          ...(draft.multi_day_registration_enabled
            ? {
                registration_units: (registrationUnitsByType[i.ticket_type_id] || []).map((u) => ({
                  event_day_ids: u.event_day_ids,
                  field_values: skipParticipantNameField
                    ? {
                        ...u.field_values,
                        [participantNameField.key]: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
                      }
                    : u.field_values,
                })),
              }
            : {}),
          ...(draft.timeslot_booking_enabled
            ? {
                timeslot_units: (timeslotUnitsByType[i.ticket_type_id] || []).map((u) => ({
                  timeslot_ids: u.timeslot_ids,
                  field_values: u.field_values,
                })),
              }
            : {}),
        })),
        return_url: `${window.location.origin}/order/`,
        terms_accepted: true,
        participant_terms_accepted: participantTermsAccepted,
        organizer_terms_accepted: organizerTermsAccepted,
        discount_code: appliedDiscount?.code || null,
      };
      const { data } = await axios.post(`${API_BASE}/orders`, payload);
      sessionStorage.removeItem(DRAFT_KEY);
      if (data.redirect_url) {
        // `redirect_url` is either our own relative order page (free/pay-on-
        // location orders — stays in whatever frame this checkout is
        // already running in, same as before) or an absolute external
        // payment-provider checkout URL (Mollie/Pay.nl). This checkout page
        // can be embedded in an iframe on an external site (e.g. a
        // WordPress registration page), and the payment provider's checkout
        // page refuses to load inside that iframe (X-Frame-Options/CSP
        // frame-ancestors) — so only the external case needs to escape to
        // the top-level window.
        if (/^https?:\/\//i.test(data.redirect_url)) {
          navigateTopLevel(data.redirect_url);
        } else {
          window.location.href = data.redirect_url;
        }
      } else {
        navigate(`/order/${data.order_id}`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Er ging iets mis";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!draft) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-400">
        <Loader2 className="h-6 w-6 mx-auto animate-spin text-svj-cyan" />
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
      <Link
        to={`/events/${draft.event_slug}`}
        className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-4"
        data-testid="checkout-back"
      >
        <ArrowLeft className="h-4 w-4" /> Terug naar event
      </Link>
      <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">Kassa</div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
        Bijna klaar
      </h1>
      <p className="mt-2 text-slate-300">
        Vul je gegevens in — we sturen de tickets naar je e-mail.
      </p>

      <form onSubmit={onSubmit} className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-svj-card border border-svj-border rounded-2xl p-6">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-svj-cyan" />
              <div className="text-sm font-semibold text-white uppercase tracking-wider">
                Klantgegevens
              </div>
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Voornaam" name="first_name" form={form} setForm={setForm} required autoComplete="given-name" testid="checkout-first-name" />
              <Field label="Achternaam" name="last_name" form={form} setForm={setForm} required autoComplete="family-name" testid="checkout-last-name" />
              <Field label="E-mailadres" name="email" form={form} setForm={setForm} type="email" required autoComplete="email" testid="checkout-email" className="sm:col-span-2" />
              <Field label="Telefoon" name="phone" form={form} setForm={setForm} type="tel" required autoComplete="tel" testid="checkout-phone" />
              <Field label="Bedrijfsnaam" name="company_name" form={form} setForm={setForm} autoComplete="organization" testid="checkout-company-name" />
              {!draft.checkout_hide_address_fields && (
                <>
                  <Field label="Straat + nr" name="street" form={form} setForm={setForm} required autoComplete="street-address" testid="checkout-street" className="sm:col-span-2" />
                  <Field label="Postcode" name="postal_code" form={form} setForm={setForm} required autoComplete="postal-code" testid="checkout-postal" />
                  <Field label="Plaats" name="city" form={form} setForm={setForm} required autoComplete="address-level2" testid="checkout-city" />
                </>
              )}
            </div>
          </div>

          {draft.companion_app_enabled && (
            <div className="bg-svj-card border border-svj-border rounded-2xl p-6" data-testid="checkout-participants">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-svj-cyan" />
                <div className="text-sm font-semibold text-white uppercase tracking-wider">
                  Deelnemersgegevens
                </div>
              </div>
              <div className="mt-5 space-y-4">
                {draft.items.map((i) =>
                  (participantsByType[i.ticket_type_id] || []).map((p, idx) => (
                    <ParticipantBlock
                      key={`${i.ticket_type_id}-${idx}`}
                      index={idx}
                      ticketName={i.ticket_name}
                      value={p}
                      onChange={(next) => setParticipant(i.ticket_type_id, idx, next)}
                      routeGroups={routeGroups}
                      routeRequired={routeGroups.length > 0}
                    />
                  )),
                )}
              </div>
            </div>
          )}

          {draft.multi_day_registration_enabled && (
            <div className="bg-svj-card border border-svj-border rounded-2xl p-6" data-testid="checkout-registration-units">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-svj-cyan" />
                <div className="text-sm font-semibold text-white uppercase tracking-wider">
                  Aanmeldgegevens
                </div>
              </div>
              {skipParticipantNameField && (
                <p className="mt-2 text-xs text-slate-400" data-testid="checkout-registration-name-autofill-note">
                  Je naam ({form.first_name.trim() || "…"} {form.last_name.trim()}) wordt automatisch overgenomen van de klantgegevens hierboven.
                </p>
              )}
              <div className="mt-5 space-y-4">
                {draft.items.map((i) =>
                  (registrationUnitsByType[i.ticket_type_id] || []).map((u, idx) => (
                    <RegistrationUnitBlock
                      key={`${i.ticket_type_id}-${idx}`}
                      index={idx}
                      ticketName={i.ticket_name}
                      value={u}
                      onChange={(next) => setRegistrationUnit(i.ticket_type_id, idx, next)}
                      days={eventDays}
                      fieldDefs={
                        skipParticipantNameField
                          ? registrationFields.filter((f) => f.id !== participantNameField.id)
                          : registrationFields
                      }
                      fieldsById={registrationFieldsById}
                    />
                  )),
                )}
              </div>
            </div>
          )}

          {draft.timeslot_booking_enabled && (
            <div className="bg-svj-card border border-svj-border rounded-2xl p-6" data-testid="checkout-timeslot-units">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-svj-cyan" />
                <div className="text-sm font-semibold text-white uppercase tracking-wider">
                  Kies je afspraak
                </div>
              </div>
              <div className="mt-5 space-y-4">
                {draft.items.map((i) =>
                  (timeslotUnitsByType[i.ticket_type_id] || []).map((u, idx) => (
                    <TimeslotBookingUnitBlock
                      key={`${i.ticket_type_id}-${idx}`}
                      index={idx}
                      ticketName={i.ticket_name}
                      value={u}
                      onChange={(next) => setTimeslotUnit(i.ticket_type_id, idx, next)}
                      timeslots={timeslots}
                      categories={timeslotCategories}
                      fieldDefs={registrationFields}
                      fieldsById={registrationFieldsById}
                    />
                  )),
                )}
              </div>
            </div>
          )}

          <div className="text-xs text-slate-500 flex items-center gap-2">
            <Lock className="h-3.5 w-3.5" />
            {allPayOnLocation
              ? "Je betaalt dit bedrag ter plekke bij het evenement — geen online betaling nodig."
              : isFreeOrder
                ? "Deze aanmelding is gratis — er is geen betaling nodig."
                : "Je betaling verloopt versleuteld via onze betaalprovider. StudioVoorJou slaat geen betaalgegevens op."}
          </div>
        </div>

        {/* Summary */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 bg-svj-card border border-svj-border rounded-2xl p-6" data-testid="checkout-summary">
            <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Overzicht</div>
            <h3 className="mt-1 text-lg font-bold text-white">{draft.event_title}</h3>
            <div className="mt-4 divide-y divide-svj-border">
              {draft.items.map((i) => {
                // Timeslot booking: the real price comes from each unit's own
                // chosen slot, never the ticket type's own (meaningless here)
                // unit_price_cents.
                const itemUnits = timeslotUnitsByType[i.ticket_type_id] || [];
                const itemUnitsSelected = itemUnits.length > 0 && itemUnits.every((u) => u.timeslot_ids?.length);
                const lineCents = draft.timeslot_booking_enabled
                  ? itemUnits.reduce((a, u) => a + timeslotUnitPriceCents(u, timeslotsById), 0)
                  : i.qty * i.unit_price_cents;
                return (
                  <div key={i.ticket_type_id} className="py-3 flex justify-between text-sm">
                    <span className="text-slate-300">
                      {i.qty}× <span className="text-white font-medium">{i.ticket_name}</span>
                    </span>
                    <span className="text-white font-semibold">
                      {draft.timeslot_booking_enabled && !itemUnitsSelected
                        ? "Kies je afspraak"
                        : lineCents === 0
                          ? "Gratis"
                          : formatPrice(lineCents)}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 border-t border-svj-border pt-4">
              {appliedDiscount ? (
                <div className="flex items-center justify-between gap-2 text-sm" data-testid="checkout-discount-applied">
                  <span className="flex items-center gap-1.5 text-emerald-400">
                    <Tag className="h-3.5 w-3.5" />
                    Code <code className="font-mono">{appliedDiscount.code}</code>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="text-emerald-400 font-semibold">-{formatPrice(appliedDiscount.discount_amount_cents)}</span>
                    <button type="button" onClick={removeDiscountCode} data-testid="checkout-discount-remove" className="text-slate-500 hover:text-white">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={discountCodeInput}
                    onChange={(e) => setDiscountCodeInput(e.target.value)}
                    placeholder="Kortingscode"
                    data-testid="checkout-discount-input"
                    className="flex-1 h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan uppercase"
                  />
                  <button
                    type="button"
                    onClick={applyDiscountCode}
                    disabled={checkingDiscount || !discountCodeInput.trim()}
                    data-testid="checkout-discount-apply"
                    className="h-10 px-4 rounded-lg border border-svj-border text-sm font-semibold text-slate-300 hover:text-white disabled:opacity-50"
                  >
                    {checkingDiscount ? <Loader2 className="h-4 w-4 animate-spin" /> : "Toepassen"}
                  </button>
                </div>
              )}
              {discountError && (
                <div className="mt-1.5 text-xs text-red-400" data-testid="checkout-discount-error">{discountError}</div>
              )}
            </div>
            <div className="mt-4 border-t border-svj-border pt-4 flex items-center justify-between">
              <span className="text-sm text-slate-300">Totaal ({totalQty} tickets)</span>
              <span className="text-2xl font-extrabold text-svj-cyan" data-testid="checkout-total">
                {draft.timeslot_booking_enabled && !allTimeslotUnitsSelected
                  ? "Kies je afspraak"
                  : isFreeOrder
                    ? "Gratis"
                    : formatPrice(payableCents)}
              </span>
            </div>
            <div className="mt-1 text-[11px] text-slate-500">Btw wordt server-side berekend uit deze inclusief-prijs.</div>
            {skipsOnlinePayment && (
              <div className="mt-3 rounded-lg border border-svj-cyan/40 bg-svj-cyan/10 px-3 py-2 text-xs text-svj-cyan" data-testid="checkout-pay-on-location-note">
                {allPayOnLocation
                  ? "Je betaalt dit bedrag ter plekke bij het evenement, niet nu online."
                  : "Gratis — geen betaling nodig."}
              </div>
            )}

            <label className="mt-6 flex items-start gap-3 cursor-pointer select-none" htmlFor="checkout-terms">
              <input
                id="checkout-terms"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                required
                data-testid="checkout-terms"
                className="mt-0.5 h-4 w-4 rounded border-svj-border bg-svj-bg text-svj-cyan focus:ring-svj-cyan focus:ring-offset-0 accent-svj-cyan"
              />
              <span className="text-xs text-slate-300 leading-relaxed">
                Ik ga akkoord met de{" "}
                <Link
                  to="/voorwaarden"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="checkout-terms-link"
                  className="text-svj-cyan hover:underline font-semibold"
                >
                  algemene voorwaarden
                </Link>{" "}
                en het{" "}
                <Link
                  to="/privacybeleid"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="checkout-privacy-link"
                  className="text-svj-cyan hover:underline font-semibold"
                >
                  privacybeleid
                </Link>{" "}
                en bevestig dat ik weet dat event-tickets niet retourneerbaar zijn.
              </span>
            </label>

            {draft.participant_terms_enabled && (
              <label className="mt-3 flex items-start gap-3 cursor-pointer select-none" htmlFor="checkout-participant-terms">
                <input
                  id="checkout-participant-terms"
                  type="checkbox"
                  checked={participantTermsAccepted}
                  onChange={(e) => setParticipantTermsAccepted(e.target.checked)}
                  required
                  data-testid="checkout-participant-terms"
                  className="mt-0.5 h-4 w-4 rounded border-svj-border bg-svj-bg text-svj-cyan focus:ring-svj-cyan focus:ring-offset-0 accent-svj-cyan"
                />
                <span className="text-xs text-slate-300 leading-relaxed">
                  Ik ga akkoord met het{" "}
                  {draft.participant_terms_url ? (
                    <a
                      href={draft.participant_terms_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="checkout-participant-terms-link"
                      className="text-svj-cyan hover:underline font-semibold"
                    >
                      deelnemersreglement
                    </a>
                  ) : (
                    "deelnemersreglement"
                  )}
                  .
                </span>
              </label>
            )}

            {draft.organizer_terms_enabled && draft.organizer_terms_text && (
              <div className="mt-3 rounded-xl border border-svj-border bg-svj-bg/60 p-4">
                <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold mb-2">
                  Aanvullende voorwaarden van de organisator
                </div>
                <p
                  className="text-xs text-slate-300 leading-relaxed whitespace-pre-line"
                  data-testid="checkout-organizer-terms-text"
                >
                  {draft.organizer_terms_text}
                </p>
                <p className="mt-2 text-[11px] text-slate-500">
                  Dit geldt uitsluitend voor dit evenement en gaat, op het punt waarop het afwijkt,
                  voor op de{" "}
                  <Link
                    to="/voorwaarden"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-svj-cyan hover:underline"
                  >
                    algemene voorwaarden
                  </Link>{" "}
                  — de rest van die voorwaarden blijft gewoon van toepassing. Wettelijke regels,
                  zoals het herroepingsrecht, worden hierdoor nooit gewijzigd.
                </p>
                <label
                  className="mt-3 flex items-start gap-3 cursor-pointer select-none"
                  htmlFor="checkout-organizer-terms"
                >
                  <input
                    id="checkout-organizer-terms"
                    type="checkbox"
                    checked={organizerTermsAccepted}
                    onChange={(e) => setOrganizerTermsAccepted(e.target.checked)}
                    required
                    data-testid="checkout-organizer-terms"
                    className="mt-0.5 h-4 w-4 rounded border-svj-border bg-svj-bg text-svj-cyan focus:ring-svj-cyan focus:ring-offset-0 accent-svj-cyan"
                  />
                  <span className="text-xs text-slate-300 leading-relaxed">
                    Ik ga akkoord met de aanvullende voorwaarden van de organisator hierboven.
                  </span>
                </label>
              </div>
            )}

            <button
              type="submit"
              disabled={
                submitting ||
                !termsAccepted ||
                (draft.participant_terms_enabled && !participantTermsAccepted) ||
                (draft.organizer_terms_enabled && !organizerTermsAccepted)
              }
              data-testid="checkout-submit"
              className={`mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full py-3 px-6 font-bold transition-all ${
                submitting ||
                !termsAccepted ||
                (draft.participant_terms_enabled && !participantTermsAccepted) ||
                (draft.organizer_terms_enabled && !organizerTermsAccepted)
                  ? "bg-svj-bg/60 border border-svj-border text-slate-500 cursor-not-allowed"
                  : "bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white hover:shadow-[0_0_24px_rgba(214,61,122,0.5)]"
              }`}
            >
              {submitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <ShoppingCart className="h-5 w-5" />
              )}
              {submitting ? "Bezig…" : skipsOnlinePayment ? "Aanmelden" : "Betalen"}
            </button>
          </div>
        </aside>
      </form>
    </div>
  );
}
